import { createTable, HISTORY_CSS } from "./BURHistoryDom";
import BURHistoryFormatter from "./BURHistoryFormatter";
import { getFreeSpinsTotals, getSpinWin } from "./BURHistoryStats";
import { comboText, HistoryText, historyText } from "./BURHistoryTexts";
import { IHistoryBonus, IHistorySessionData } from "./BURHistoryTypes";

type SummaryRow = [string, string];

export function createSummaryTable(
    session: IHistorySessionData,
    formatter: BURHistoryFormatter
): HTMLTableElement {
    return createTable(
        null,
        getSummaryRows(session, formatter),
        HISTORY_CSS.summaryTable
    );
}

/**
 * The feature colours of the free spins sessions, e.g. "Blue + Yellow". Sessions with different combos are
 * separated by commas. Feature names are translated for display only (jackpotsEnabled uses the raw server value).
 */
function getCombosText(bonusInfo: IHistoryBonus[]): string {
    const combos = bonusInfo
        .filter((bonus) => bonus.combo && bonus.combo.length)
        .map((bonus) => bonus.combo!.map(comboText).join(" + "));
    return combos.filter((combo, i) => combos.indexOf(combo) === i).join(", ");
}

/**
 * Round id, times and bet, then the number of free spins and their combo (if any) right above the win rows:
 * Total win, then its breakdown when there is one: Base spin win, Replay spin win (if any), Free spins win (if any).
 * Jackpots are part of the free spins win, so they have no row of their own. Balances last.
 */
export function getSummaryRows(
    session: IHistorySessionData,
    formatter: BURHistoryFormatter
): SummaryRow[] {
    const gameData = session.playData.gameData;
    const stats = gameData.spinStats;
    const replay = gameData.replay;
    // All free spins together, whether triggered by the paid spin or by the replay spin
    const bonusInfo = [
        ...(gameData.bonusInfo || []),
        ...(replay?.bonusInfo || [])
    ];
    const freeSpins = getFreeSpinsTotals(bonusInfo);
    const freeSpinsCount = freeSpins.count || (stats ? stats.freeSpinCount : 0);
    const hasFreeSpins = freeSpinsCount > 0;
    const combos = getCombosText(bonusInfo);
    const money = (value: number | undefined) => formatter.money(value);
    const row = (label: HistoryText, value: string): SummaryRow => [
        historyText(label),
        value
    ];

    return [
        row("roundId", session.playId),
        row("started", formatter.date(session.createdAt)),
        row("ended", formatter.date(session.endAt)),
        row("totalBet", money(formatter.bet)),
        ...(hasFreeSpins ? [row("freeSpins", `${freeSpinsCount}`)] : []),
        ...(combos ? [row("freeSpinsCombo", combos)] : []),
        row("totalWin", money(stats ? stats.totalWon : gameData.creditsWon)),
        ...(replay || hasFreeSpins
            ? [row("baseSpinWin", money(getSpinWin(gameData)))]
            : []),
        // Only the replay spin itself: the engine folds the replay's free spins into replay.creditsWon
        ...(replay ? [row("replaySpinWin", money(getSpinWin(replay)))] : []),
        // spinStats.freeWon also covers both, for rounds without per-session data
        ...(hasFreeSpins
            ? [
                  row(
                      "freeSpinsWin",
                      money(
                          freeSpins.count
                              ? freeSpins.win
                              : stats
                                ? stats.freeWon
                                : 0
                      )
                  )
              ]
            : []),
        row("balanceBefore", money(session.balanceBeforeBet)),
        row("balanceAfterBet", money(session.balanceAfterBet)),
        row("balanceAfter", money(session.balanceAfterRoundEnd))
    ];
}
